import type { TFunction } from "i18next";
import type { DataTableFilterGroup } from "../data-table/data-table.types";
import { ACTIVE_STATUS_FILTER, CATALOGUE_FIELDS, type CatalogueField } from "./admin-catalogue-rows";
import { ADMIN_PRODUCTS_COLUMN_IDS, ADMIN_WEB_STATUSES, webStatusLabel } from "./admin-products-columns";

export const ADMIN_CATALOGUE_FILTER_GROUP_IDS = {
  STATUS: "status",
  BADGES: "badges",
  MISSING: "missing",
} as const;

interface CatalogueFilterGroupsInput {
  t: TFunction;
  badgeOptions: readonly string[];
  missing: readonly CatalogueField[];
  onMissingChange: (missing: CatalogueField[]) => void;
}

const isCatalogueField = (value: string): value is CatalogueField => (CATALOGUE_FIELDS as readonly string[]).includes(value);

/**
 * Status and badges drive their table columns; "Missing" is controlled by the
 * view because the coverage metrics toggle the same selection.
 */
export const buildCatalogueFilterGroups = ({
  t,
  badgeOptions,
  missing,
  onMissingChange,
}: CatalogueFilterGroupsInput): DataTableFilterGroup[] => [
  {
    id: ADMIN_CATALOGUE_FILTER_GROUP_IDS.STATUS,
    display: "dropdown",
    label: t("admin.productCatalogue.filters.statusLabel"),
    column: ADMIN_PRODUCTS_COLUMN_IDS.WEB_STATUS,
    allOptionLabel: t("admin.productCatalogue.filters.allStatuses"),
    options: [
      { label: t("admin.productCatalogue.filters.active"), value: ACTIVE_STATUS_FILTER },
      ...ADMIN_WEB_STATUSES.map((status) => ({ label: webStatusLabel(t, status), value: status })),
    ],
  },
  {
    id: ADMIN_CATALOGUE_FILTER_GROUP_IDS.BADGES,
    display: "dropdown",
    label: t("admin.productCatalogue.filters.badgesLabel"),
    column: ADMIN_PRODUCTS_COLUMN_IDS.BADGES,
    allOptionLabel: t("admin.productCatalogue.filters.allBadges"),
    options: badgeOptions.map((badge) => ({ label: badge, value: badge })),
  },
  {
    id: ADMIN_CATALOGUE_FILTER_GROUP_IDS.MISSING,
    display: "buttons",
    label: t("admin.productCatalogue.filters.missingLabel"),
    options: CATALOGUE_FIELDS.map((field) => ({ label: t(`admin.productCatalogue.metrics.${field}`), value: field })),
    selected: [...missing],
    onSelectedChange: (next) => onMissingChange(next.filter(isCatalogueField)),
  },
];
